import Dashelement from "components/Dashboard/Dashelement";
import DiscountConfigForm from 'components/Forms/DiscountConfigForm';
import CommissionCalculator from "components/Dots/CommissionCalculator";
import HostRewardTypeContext from "context/HostRewardTypeContext";
import { ErrorMessage, Field, Form, Formik } from "formik";
import InfoText from "Pages/dashboard/Profile/InfoTextComponent";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import * as yup from "yup";
import { HOST_REWARD_TYPES, normalizeRewardType } from "../constants";
import "./storedetail.css";
import { EditOutlined } from "@ant-design/icons";
// Validation schema - discountGiven is optional since discount can be disabled
const schema = yup.object().shape({
  demoCommission: yup
    .number()
    .required("required")
    .test("is-decimal", "invalid decimal", (value) =>
      (value + "").match(/^\d*\.?\d*$/)
    ),
  salesCommission: yup
    .number()
    .required("required")
    .test("is-decimal", "invalid decimal", (value) =>
      (value + "").match(/^\d*\.?\d*$/)
    ),
  discountGiven: yup
    .number()
    .nullable()
    .test("is-decimal", "invalid decimal", (value) =>
      value === null || value === undefined || (value + '').match(/^\d*\.?\d*$/)
    ),
});

const StoreDetail = (props) => {
  const { getStoreRewardType, updateStoreRewardType } = useContext(
    HostRewardTypeContext
  );
  const showViewMode = props.isCompleted && !props.isEditing;
  const data = props.data || {};
  const store = data.store || data || {};
  const readOnly = showViewMode;
  const [initialRewardType, setInitialRewardType] = useState(
    normalizeRewardType(props.data?.store?.host_reward_type || props.data?.rewardType)
  );
  const [rewardTypeLoading, setRewardTypeLoading] = useState(false);
  const [rewardTypeSaving, setRewardTypeSaving] = useState(false);
  const [rewardTypeError, setRewardTypeError] = useState("");
  // AFTER
  const hasHydratedRewardType = useRef(false);
  const prevDataRef = useRef(props.data);

  // Reset hydration guard when props.data reference changes (e.g. after save→edit cycle)
  useEffect(() => {
    if (prevDataRef.current !== props.data) {
      hasHydratedRewardType.current = false;
      prevDataRef.current = props.data;
    }
}, [props.data]);
  const [discountEnabled, setDiscountEnabled] = useState(
    props.data?.store?.is_guest_discount_active ?? props.data?.is_guest_discount_active ?? true
  );
  // In onboarding, start with null/undefined to trigger defaults
  // Only use actual config if it exists from props
  const [discountConfig, setDiscountConfig] = useState(
    props.data?.store?.discount_config || props.data?.discount_config || null
  );
  const discountConfigFormRef = useRef(null);

  const getInitialNumericValue = (...candidates) => {
    for (let index = 0; index < candidates.length; index += 1) {
      const value = candidates[index];
      if (value === undefined || value === null || value === "") continue;
      if (!props.isCompleted && (value === 0 || value === "0")) return null;
      return value;
    }
    return null;
  };

  // Sync discountEnabled state when props.data changes
  useEffect(() => {
    // Only update if the value is explicitly set (not undefined/null)
    if (props.data?.store?.is_guest_discount_active !== undefined) {
      setDiscountEnabled(props.data.store.is_guest_discount_active);
    } else if (props.data?.is_guest_discount_active !== undefined) {
      setDiscountEnabled(props.data.is_guest_discount_active);
    } else {
      setDiscountEnabled(true);
    }
    // If undefined, keep the default (true) from useState
  }, [props.data?.store?.is_guest_discount_active, props.data?.is_guest_discount_active]);

  // Sync discountConfig when props.data changes
  // In onboarding, use null if no config exists to trigger defaults
  useEffect(() => {
    const config = props.data?.store?.discount_config || props.data?.discount_config;
    if (config && Object.keys(config).length > 0) {
      setDiscountConfig(config);
    } else {
      // Set to null in onboarding to trigger default values
      setDiscountConfig(null);
    }
  }, [props.data?.store?.discount_config, props.data?.discount_config]);

  useEffect(() => {
    let isMounted = true;

    const fetchRewardType = async () => {
      setRewardTypeLoading(true);
      try {
        const response = await getStoreRewardType();
        const hostRewardType =
          response?.data?.host_reward_type ||
          response?.data?.data?.host_reward_type;
        const normalized = normalizeRewardType(
          hostRewardType ?? props.data?.store?.host_reward_type ?? props.data?.rewardType
        );
        if (isMounted && !hasHydratedRewardType.current) {
          setInitialRewardType(normalized);
          hasHydratedRewardType.current = true;
        }
        if (isMounted) {
          setRewardTypeError("");
        }
      } catch (error) {
        // Handle 404 specifically - user might not have StoreAdmins record (common in onboarding)
        if (error?.response?.status === 404) {
          // Silently fall back to props data - this is expected during onboarding
          const fallback = normalizeRewardType(props.data?.rewardType);
          if (isMounted && !hasHydratedRewardType.current) {
            setInitialRewardType(fallback);
            hasHydratedRewardType.current = true;
          }
          if (isMounted) {
            setRewardTypeError("");
          }
        } else {
          if (isMounted && !hasHydratedRewardType.current) {
            const fallback = normalizeRewardType(props.data?.rewardType);
            setInitialRewardType(fallback);
            hasHydratedRewardType.current = true;
          }
          if (isMounted) {
            setRewardTypeError(
              "Unable to load reward preference. Defaults to cash."
            );
          }
        }
      } finally {
        if (isMounted) {
          setRewardTypeLoading(false);
        }
      }
    };

    fetchRewardType();

    return () => {
      isMounted = false;
    };
    // props.data dependency ensures updated when wizard data changes
  }, [getStoreRewardType, props.data?.rewardType]);

  /**
   * Handles discount config changes from DiscountConfigForm.
   *
   * @param {Object} config - Transformed discount configuration
   * @returns {void}
   */
  const handleDiscountConfigChange = config => {
    setDiscountConfig(config);
  };

  const handleSubmit = (values) => {
    values.demoCommission = parseFloat(values.demoCommission);
    values.salesCommission = parseFloat(values.salesCommission);
    // Parse discountGiven only if it exists and discount is enabled
    if (values.discountGiven && discountEnabled) {
      values.discountGiven = parseFloat(values.discountGiven);
    } else {
      values.discountGiven = null;
    }
    values.no_show_demo_commission = parseFloat(values.no_show_demo_commission);
    values.rewardType = normalizeRewardType(values.rewardType);

    // Always get discount config from the form, regardless of enabled/disabled status
    let finalDiscountConfig = {};
    if (discountConfigFormRef.current && discountConfigFormRef.current.getValues) {
      try {
        finalDiscountConfig = discountConfigFormRef.current.getValues();
        // Include discount and discount_type from form values
        finalDiscountConfig.discount = values.discountGiven || null;
        finalDiscountConfig.discount_type = values.discountType || null;
      } catch (error) {
        // Fallback to stored config
        finalDiscountConfig = {
          ...discountConfig,
          discount: values.discountGiven || null,
          discount_type: values.discountType || null
        };
      }
    } else {
      // Fallback to stored config
      finalDiscountConfig = {
        ...discountConfig,
        discount: values.discountGiven || null,
        discount_type: values.discountType || null
      };
    }

    // Always include discount_config and is_guest_discount_active in the payload
    values.discount_config = finalDiscountConfig;
    values.is_guest_discount_active = discountEnabled;

    //redirect the user to settings page
    props.next(values, false, true);
  };

  const getRewardTypeLabel = (type) => {
    const t = normalizeRewardType(type);
    if (t === HOST_REWARD_TYPES.CASH.VALUE) return HOST_REWARD_TYPES.CASH.LABEL;
    if (t === HOST_REWARD_TYPES.STORE_CREDIT.VALUE) return HOST_REWARD_TYPES.STORE_CREDIT.LABEL;
    if (t === HOST_REWARD_TYPES.GIFT_CARD.VALUE) return HOST_REWARD_TYPES.GIFT_CARD.LABEL;
    return t || "—";
  };

  return (
    <>
    <Formik
      enableReinitialize
      initialValues={{
        ...props.data,
        demoCommission: getInitialNumericValue(store.demo_commission, store.demoCommission),
        commissionType: store.demo_commission_type ?? store.commissionType ?? "AMOUNT",
        salesCommission: getInitialNumericValue(store.demo_sales_commission, store.salesCommission),
        salesCommissionType: store.demo_sales_commission_type ?? store.salesCommissionType ?? "AMOUNT",
        discountGiven: getInitialNumericValue(store.discount_given, store.discountGiven),
        discountType: store.discount_type ?? store.discountType ?? "AMOUNT",
        no_show_demo_commission: getInitialNumericValue(store.no_show_demo_commission), // already snake in field
        no_show_demo_commission_type: store.no_show_demo_commission_type ?? store.no_show_demo_commission_type ?? "AMOUNT", // already snake in field
        paymentFrequency: store.payment_frequency ?? store.paymentFrequency ?? 1,
        rewardType: initialRewardType,
        is_guest_discount_active: store.is_guest_discount_active ?? data.is_guest_discount_active ?? true
      }}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({ values, touched, errors, setFieldValue }) => {
        const currentRewardType = normalizeRewardType(values?.rewardType);
        const allowedRewardTypes = props.data?.store?.allowed_host_reward_types || props.data?.allowed_host_reward_types || [];

        /**
         * Gets the CSS className for the reward type label span based on selection state.
         *
         * @param {string} rewardType - The reward type value to check
         * @returns {string} "fw-bold" if the reward type is currently selected, "text-muted" otherwise
         */
        const getSpanClassName = rewardType => {
          if (currentRewardType === rewardType) {
            return 'fw-bold';
          }
          return 'text-muted';
        };

        /**
         * Renders reward type radio button options based on allowed reward types.
         * Filters the available reward types based on allowed_host_reward_types from the store.
         * If no allowed types are specified, shows all options for backward compatibility.
         *
         * @returns {Array<JSX.Element>} Array of label elements containing radio buttons for each allowed reward type
         */
        const renderRewardTypeOptions = () => {
          const rewardTypeConfig = [
            { type: HOST_REWARD_TYPES.CASH.VALUE, label: HOST_REWARD_TYPES.CASH.LABEL },
            { type: HOST_REWARD_TYPES.STORE_CREDIT.VALUE, label: HOST_REWARD_TYPES.STORE_CREDIT.LABEL },
            { type: HOST_REWARD_TYPES.GIFT_CARD.VALUE, label: HOST_REWARD_TYPES.GIFT_CARD.LABEL }
          ];

          const optionsToShow = [];

          // If no allowed types specified, show all (backward compatibility)
          if (!Array.isArray(allowedRewardTypes) || allowedRewardTypes.length === 0) {
            rewardTypeConfig.forEach(config => {
              optionsToShow.push(config);
            });
          } else {
            // Only show allowed types
            rewardTypeConfig.forEach(config => {
              if (allowedRewardTypes.includes(config.type)) {
                optionsToShow.push(config);
              }
            });
          }

          return optionsToShow.map(config => {
            return (
              <label
                key={config.type}
                className="d-flex align-items-center"
                style={{ cursor: 'pointer', fontSize: '1.1rem' }}
              >
                <input
                  type="radio"
                  name="rewardType"
                  value={config.type}
                  checked={currentRewardType === config.type}
                  onChange={e => handleRewardTypeChange(e.target.value)}
                  disabled={rewardTypeLoading || rewardTypeSaving || readOnly}
                  style={{ marginRight: '0.6rem', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span className={getSpanClassName(config.type)}>
                  {config.label}
                </span>
              </label>
            );
          });
        };

        /**
         * Renders a loading spinner when reward type is being loaded or saved.
         *
         * @returns {JSX.Element|null} Spinner component if loading/saving, null otherwise
         */
        const renderLoadingSpinner = () => {
          if (rewardTypeLoading || rewardTypeSaving) {
            return (
              <Spinner
                animation="border"
                size="sm"
                className="text-secondary"
              />
            );
          }
          return null;
        };

        /**
         * Renders an error message if there's an error with the reward type operation.
         *
         * @returns {JSX.Element|null} Error message div if error exists, null otherwise
         */
        const renderErrorMessage = () => {
          if (rewardTypeError) {
            return (
              <div className="text-danger small mt-2">
                {rewardTypeError}
              </div>
            );
          }
          return null;
        };

        const handleRewardTypeChange = async (newType) => {
          if (newType === currentRewardType) {
            return;
          }

          setRewardTypeSaving(true);
          setRewardTypeError("");
          try {
            await updateStoreRewardType(newType);
            setFieldValue("rewardType", newType);
          } catch (toggleError) {
            // Handle 404 - user might not have StoreAdmins record (common in onboarding)
            // In this case, just update the form value - it will be saved when form is submitted
            if (toggleError?.response?.status === 404) {
              setFieldValue("rewardType", newType);
              setRewardTypeError("");
            } else {
              setRewardTypeError(
                "Unable to update reward preference. Please try again."
              );
            }
          } finally {
            setRewardTypeSaving(false);
          }
        };

        return (
          <Form>
            {/* ── Two-column layout: Form (left) + Calculator (right) ── */}
            <div className="storedetail-layout-row">

              {/* Left column — original form content, nothing changed inside */}
              <div className="storedetail-form-col">
                <Dashelement className="features basic-info-wrapper">
                  <div className="container">
                    <div className="information-text-wrapper">
                      <h3>Store Information</h3>
                      <p>
                        Please enter the following details for your store real customers and
                        guests to receive their shares.
                      </p>
                    </div>
                    <div className="row mt-4">
                      <div className="col col-md-12">
                        <label className="mb-2">
                          <strong>Real Customer Reward Type</strong>
                          <InfoText text="Choose how real customers are rewarded: cash payouts, store credit, or gift cards." />
                        </label>
                        <div className="d-flex align-items-center flex-wrap mt-2" style={{ gap: "1.5rem" }}>
                          {renderRewardTypeOptions()}
                          {renderLoadingSpinner()}
                        </div>
                        {renderErrorMessage()}
                      </div>
                    </div>
                    <div className="row  mt-4">
                      <div className="col col-md-3">
                        <Field
                          placeholder="Video meeting commission (Eg. 12.99)"
                          type="text"
                          name="demoCommission"
                          readOnly={readOnly}
                          className={`form-control ${
                            touched.demoCommission && errors.demoCommission
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <ErrorMessage
                          component="div"
                          name="demoCommission"
                          className="invalid-feedback"
                        />
                      </div>
                      <div className="col col-sm-4 col-md-2 d-flex flex-column justify-content-center align-items-start">
                        <label className="mb-1 display-flex">
                          <Field
                            type="radio"
                            name="commissionType"
                            value="AMOUNT"
                            disabled={readOnly}
                          />{" "}
                          $
                        </label>
                        <ErrorMessage
                          component="div"
                          name="commissionType"
                          className="invalid-feedback"
                        />
                      </div>
                      <div className="col col-md-6 d-flex align-items-center">
                        <InfoText text="How much real customers will be paid for each meeting they are in." />
                      </div>
                    </div>
                    <div className="row mt-4">
                      <div className="col col-md-3">
                        <Field
                          placeholder="Sales commission (Eg. 12.99)"
                          type="text"
                          name="salesCommission"
                          readOnly={readOnly}
                          className={`form-control ${
                            touched.salesCommission && errors.salesCommission
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <ErrorMessage
                          component="div"
                          name="salesCommission"
                          className="invalid-feedback"
                        />
                      </div>
                      <div className="col col-sm-4 col-md-2 d-flex flex-column justify-content-center align-items-start">
                        <label className="mb-1 display-flex">
                          <Field
                            type="radio"
                            name="salesCommissionType"
                            value="AMOUNT"
                            disabled={readOnly}
                          />{" "}
                          $
                        </label>
                        <label className="mb-1 display-flex">
                          <Field
                            type="radio"
                            name="salesCommissionType"
                            value="PERCENTAGE"
                            disabled={readOnly}
                          />{" "}
                          %
                        </label>
                        <ErrorMessage
                          component="div"
                          name="salesCommissionType"
                          className="invalid-feedback"
                        />
                      </div>
                      <div className="col col-md-6 d-flex align-items-center">
                        <InfoText text="How much hosts will be paid on sales that their meetings generate." />
                      </div>
                    </div>
                    <div className="row mt-4 ">
                      <div className="col col-md-3">
                        <Field
                          placeholder="No show commission (Eg. 12.99)"
                          type="text"
                          name="no_show_demo_commission"
                          readOnly={readOnly}
                          className={`form-control ${
                            touched.no_show_demo_commission &&
                            errors.no_show_demo_commission
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <ErrorMessage
                          component="div"
                          name="no_show_demo_commission"
                          className="invalid-feedback"
                        />
                      </div>
                      <div className="col col-sm-4 col-md-2 d-flex flex-column justify-content-center align-items-start">
                        <label className="mb-1 display-flex">
                          <Field
                            type="radio"
                            name="no_show_demo_commission_type"
                            value="AMOUNT"
                            disabled={readOnly}
                          />{" "}
                          $
                        </label>
                        <ErrorMessage
                          component="div"
                          name="no_show_demo_commission_type"
                          className="invalid-feedback"
                        />
                      </div>
                      <div className="col col-md-6 d-flex align-items-center">
                        <InfoText text="How much real customers will be paid for each meeting they are in, but no guest shows up." />
                      </div>
                    </div>
                    <div className="row mt-4">
                      <div className="col col-md-12 mb-2">
                        <label className="d-flex align-items-center" style={{ cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={discountEnabled}
                            onChange={(e) => setDiscountEnabled(e.target.checked)}
                            disabled={readOnly}
                            style={{ marginRight: '0.5rem', width: '18px', height: '18px', cursor: readOnly ? 'default' : 'pointer' }}
                          />
                          <span>Enable Discount</span>
                        </label>
                      </div>
                      <div className="col col-md-3">
                        <Field
                          placeholder="Discount (Eg. 12.99)"
                          type="text"
                          name="discountGiven"
                          readOnly={readOnly}
                          className={`form-control ${
                            touched.discountGiven && errors.discountGiven
                              ? "is-invalid"
                              : ""
                          }`}
                          disabled={!discountEnabled}
                        />
                        <ErrorMessage
                          component="div"
                          name="discountGiven"
                          className="invalid-feedback"
                        />
                      </div>
                      <div className="col col-sm-4 col-md-2 d-flex flex-column justify-content-center align-items-start">
                        <label className="mb-1 display-flex">
                          <Field type="radio" name="discountType" value="AMOUNT" disabled={!discountEnabled || readOnly} />{" "}
                          $
                        </label>
                        <label className="mb-1 display-flex">
                          <Field
                            type="radio"
                            name="discountType"
                            value="PERCENTAGE"
                            disabled={!discountEnabled || readOnly}
                          />{" "}
                          %
                        </label>
                        <ErrorMessage
                          component="div"
                          name="discountType"
                          className="invalid-feedback"
                        />
                      </div>
                      <div className='col col-md-6 d-flex flex-column align-items-start'>
                        <InfoText text="How much of a discount guests receive if they buy after their meeting." />
                      </div>
                      <div className="col col-md-12 mt-3">
                        <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef', opacity: discountEnabled ? 1 : 0.6 }}>
                          <DiscountConfigForm
                            ref={discountConfigFormRef}
                            discountConfig={discountConfig}
                            isOnboarding={true}
                            inline={true}
                            onChange={handleDiscountConfigChange}
                            disabled={!discountEnabled || readOnly}
                          />
                        </div>
                      </div>
                    </div>
                    {/* <div className="row mt-4">
                      <div className="col col-md-4">
                        <label>Select the day for payout release</label>
                      </div>
                      <div className="col col-md-3">
                        <Field
                          as="select"
                          name="paymentFrequency"
                          defaultValue={0}
                          className={`form-control ${
                            touched.paymentFrequency && errors.paymentFrequency
                              ? "is-invalid"
                              : ""
                          }`}
                        >
                          <option defaultValue={0} value={0}>
                            Monday
                          </option>
                          <option value={1}>Tuesday</option>
                          <option value={2}>Wednesday</option>
                          <option value={3}>Thursday</option>
                          <option value={4}>Friday</option>
                          <option value={5}>Saturday</option>
                          <option value={6}>Sunday</option>
                        </Field>
                        <ErrorMessage
                          component="div"
                          name="paymentFrequency"
                          className="invalid-feedback"
                        />
                      </div>
                    </div> */}
                  </div>
                </Dashelement>
                <div className="button-place text-end d-flex gap-2 justify-content-end">
                  {readOnly ? (
                    <>
                      {props.onStartEdit && <button type="button" className="btn btn-outline-primary" onClick={props.onStartEdit}><EditOutlined />Edit</button>}
                  {props.onNextStep && <button type="button" className="btn btn-primary" onClick={props.onNextStep}>Next</button>}
                    </>
                  ) : (
                    <>
                      {props.isEditing && props.onCancelEdit && (
                        <button type="button" className="btn btn-outline-secondary" onClick={props.onCancelEdit}>Cancel</button>
                      )}
                      <button className="btn btn-primary" type="submit">
                        {props.isEditing ? "Save" : "Next"}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Right column — Commission Calculator (sticky, read-only guidance tool) */}
              <div className="storedetail-calc-col">
                <div className="storedetail-calc-sticky">
                  <div className="storedetail-calc-bridge">
                    <span className="storedetail-bridge-icon">⟵</span>
                    <span className="storedetail-bridge-text">
                      Use the calculator to find the right values, then enter them in the form
                    </span>
                  </div>
                  <div className="storedetail-calc-card">
                    <CommissionCalculator />
                  </div>
                </div>
              </div>

            </div>
          </Form>
        );
      }}
    </Formik>
    </>
  );
};

export default StoreDetail;